import { useEffect, useMemo, useState } from "react";
import { Bell, ChevronDown, Loader2 } from "lucide-react";
import AxiosInstance from "@/utils/AxiosInstance";
import { formatDateTimeNew } from "@/utils/helpers";
import { cn } from "@/lib/utils";

type NotificationPreferences = Record<string, unknown>;

type EventPreference = {
  key: string;
  label: string;
  enabled: boolean;
};

type PreferenceView = {
  emailEnabled: boolean;
  smsEnabled: boolean;
  snooze: { primary: string; until?: string } | null;
  quietHours: string | null;
  events: EventPreference[];
};

const EVENT_PREFERENCE_LABELS: Record<string, string> = {
  NO_RPM_READING_3_DAYS: "No RPM Reading · 3 Days",
  NO_CARE_PLAN_MONTHLY: "No Care Plan Monthly",
  RPM_ACHIEVEMENT_16PLUS: "RPM Achievement 16+",
  DEVICE_READING_RECEIVED: "Device Reading Received",
};

const SNOOZE_PRESET_LABELS: Record<string, string> = {
  "1h": "1 hour",
  "4h": "4 hours",
  "24h": "24 hours",
  custom: "Custom",
};

const prefLabelClass =
  "text-[10px] font-medium uppercase tracking-wide text-[#9CA3AF]";

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function minutesToHHmm(total: number | string | null | undefined): string {
  if (total == null || total === "") return "";
  const n = typeof total === "string" ? parseInt(total, 10) : total;
  if (!Number.isFinite(n)) return "";
  const m = ((n % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${pad2(h)}:${pad2(min)}`;
}

function quietTimeFromPayload(
  p: NotificationPreferences,
  minuteKey: "quietHoursStartMinute" | "quietHoursEndMinute",
  timeKey: "quietHoursStartTime" | "quietHoursEndTime",
): string {
  const rawMin = p?.[minuteKey];
  if (rawMin != null && rawMin !== "" && Number.isFinite(Number(rawMin))) {
    return minutesToHHmm(Number(rawMin));
  }
  const t = p?.[timeKey];
  if (t == null || String(t).trim() === "") return "";
  return String(t).slice(0, 5);
}

function formatSnoozeUntil(value: unknown): string {
  if (value == null || String(value).trim() === "") return "";
  const formatted = formatDateTimeNew(String(value));
  return formatted ? `until ${formatted}` : "";
}

function resolvePreferencesPayload(data: unknown): NotificationPreferences | null {
  const root = (data as { data?: unknown })?.data ?? data;
  if (!root || typeof root !== "object") return null;
  const record = root as Record<string, unknown>;
  const prefs = record.preferences ?? record.profile ?? record;
  return prefs && typeof prefs === "object" ? (prefs as NotificationPreferences) : null;
}

function buildPreferenceView(prefs: NotificationPreferences | null): PreferenceView | null {
  if (!prefs) return null;

  const snoozePreset = String(prefs.snoozePreset ?? prefs.lastSnoozePreset ?? "").trim();
  const snoozeUntilRaw = prefs.snoozeUntil;
  const snoozeUntil = formatSnoozeUntil(snoozeUntilRaw);
  let snooze: PreferenceView["snooze"] = null;

  if (snoozePreset || snoozeUntilRaw) {
    if (snoozePreset === "custom" || (!snoozePreset && snoozeUntilRaw)) {
      const start = formatDateTimeNew(String(prefs.snoozeStartAt ?? "")) || "";
      const end = formatSnoozeUntil(snoozeUntilRaw);
      snooze = {
        primary: start || "Custom",
        until: end || undefined,
      };
    } else {
      snooze = {
        primary: SNOOZE_PRESET_LABELS[snoozePreset] ?? snoozePreset,
        until: snoozeUntil || undefined,
      };
    }
  }

  let quietHours: string | null = null;
  if (prefs.quietHoursEnabled === true) {
    const start = quietTimeFromPayload(prefs, "quietHoursStartMinute", "quietHoursStartTime");
    const end = quietTimeFromPayload(prefs, "quietHoursEndMinute", "quietHoursEndTime");
    if (start && end) quietHours = `${start} – ${end}`;
    else if (start || end) quietHours = start || end;
  }

  const eventPrefs =
    (prefs.eventPreferences as Record<string, { enabled?: boolean }> | undefined) ?? {};

  const events = Object.entries(EVENT_PREFERENCE_LABELS).map(([key, label]) => ({
    key,
    label,
    enabled: eventPrefs[key]?.enabled ?? true,
  }));

  return {
    emailEnabled: prefs.emailEnabled === true,
    smsEnabled: prefs.smsEnabled === true,
    snooze,
    quietHours,
    events,
  };
}

function PrefField({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <p className={prefLabelClass}>{label}</p>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function YesNoValue({ enabled }: { enabled: boolean }) {
  return (
    <p
      className={cn(
        "text-sm font-semibold",
        enabled ? "text-[#16A34A]" : "text-[#9CA3AF]",
      )}
    >
      {enabled ? "Yes" : "No"}
    </p>
  );
}

function EventPreferencePill({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <div
      className={cn(
        "inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1",
        enabled
          ? "border-[#86EFAC] bg-[#F0FDF4] text-[#15803D]"
          : "border-[#E5E7EB] bg-[#F9FAFB] text-[#9CA3AF]",
      )}
    >
      <span
        className={cn(
          "h-1.5 w-1.5 shrink-0 rounded-full",
          enabled ? "bg-[#22C55E]" : "bg-[#D1D5DB]",
        )}
        aria-hidden
      />
      <span className="truncate text-[11px] font-medium leading-tight">{label}</span>
    </div>
  );
}

type Props = {
  patientId?: string;
};

export function PatientNotificationPreferencesSection({ patientId }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!patientId) {
      setPreferences(null);
      setLoadError(false);
      return;
    }

    let cancelled = false;

    const loadPreferences = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const res = await AxiosInstance().get(
          `/notifications/patients/${patientId}/preferences`,
        );
        if (cancelled) return;
        setPreferences(resolvePreferencesPayload(res?.data));
      } catch {
        if (!cancelled) {
          setPreferences(null);
          setLoadError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadPreferences();

    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const view = useMemo(() => buildPreferenceView(preferences), [preferences]);

  return (
    <>
      <div className="flex w-full items-center gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
              open ? "rotate-0" : "-rotate-90"
            }`}
          />
          <Bell className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm font-semibold">Preferences</span>
        </button>
      </div>
      {open ? (
        <div className="px-4 pb-4">
          {loading ? (
            <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading preferences...
            </div>
          ) : loadError ? (
            <p className="py-1 text-sm text-muted-foreground">Unable to load preferences</p>
          ) : !view ? (
            <p className="py-1 text-sm text-muted-foreground">No preferences found</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                <PrefField label="Email Enabled">
                  <YesNoValue enabled={view.emailEnabled} />
                </PrefField>
                <PrefField label="SMS Enabled">
                  <YesNoValue enabled={view.smsEnabled} />
                </PrefField>

                {view.snooze ? (
                  <PrefField label="Snooze">
                    <p className="text-sm font-semibold text-[#111827]">{view.snooze.primary}</p>
                    {/* {view.snooze.until ? (
                      <p className="mt-0.5 text-xs text-[#9CA3AF]">{view.snooze.until}</p>
                    ) : null} */}
                  </PrefField>
                ) : null}

                {view.quietHours ? (
                  <PrefField label="Quiet Hours">
                    <p className="text-sm font-semibold text-[#111827]">{view.quietHours}</p>
                  </PrefField>
                ) : null}
              </div>

              {view.events.length > 0 ? (
                <div className="border-t border-dashed border-border/70 pt-4">
                  <p className={prefLabelClass}>Event Preferences</p>
                  <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {view.events.map((event) => (
                      <EventPreferencePill
                        key={event.key}
                        label={event.label}
                        enabled={event.enabled}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </>
  );
}
